import { Platform } from 'react-native';
import { ReviewItem } from '../types';
import {
  OnDemandItemInput,
  OnDemandItemUpdate,
} from './reviewCoreApi';
import { buildOfflineOnDemandReviewItem } from './reviewOfflineEntityFactories';

export type ReviewOnDemandItemServiceDependencies = {
  offlineReviewService: any;
  onlineApi: {
    getOnDemandItems(reviewId: number): Promise<ReviewItem[]>;
    createOnDemandItem(reviewId: number, data: OnDemandItemInput): Promise<ReviewItem>;
    updateOnDemandItem(
      reviewId: number,
      itemId: number,
      data: OnDemandItemUpdate
    ): Promise<ReviewItem>;
    deleteOnDemandItem(reviewId: number, itemId: number): Promise<void>;
  };
  getIsOnline: () => boolean;
  useOfflineWorkflow?: () => boolean;
  isWeb?: () => boolean;
};

export class ReviewOnDemandItemService {
  constructor(private readonly deps: ReviewOnDemandItemServiceDependencies) {}

  async getOnDemandItems(reviewId: number): Promise<ReviewItem[]> {
    return this.deps.onlineApi.getOnDemandItems(reviewId);
  }

  async createOnDemandItem(
    reviewId: number,
    data: OnDemandItemInput,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewItem> {
    const questionsSchema = data.questionsSchema && typeof data.questionsSchema === 'object'
      ? data.questionsSchema as Record<string, unknown>
      : {};
    const normalizedData: OnDemandItemInput = {
      ...data,
      questionsSchema: {
        ...questionsSchema,
        findingClassification: questionsSchema.findingClassification || { mode: 'off' },
      },
    };

    if (!options?.forceOnline && (this.deps.useOfflineWorkflow?.() || !this.deps.getIsOnline())) {
      return this.createOfflineOnDemandItem(reviewId, normalizedData);
    }

    try {
      return await this.deps.onlineApi.createOnDemandItem(reviewId, normalizedData);
    } catch (error) {
      if (options?.forceOnline) {
        throw error;
      }

      if (this.isWeb()) {
        throw error;
      }

      return this.createOfflineOnDemandItem(reviewId, normalizedData, error);
    }
  }

  async updateOnDemandItem(
    reviewId: number,
    itemId: number,
    data: OnDemandItemUpdate,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewItem> {
    try {
      if (
        !options?.forceOnline &&
        (this.deps.useOfflineWorkflow?.() || !this.deps.getIsOnline()) &&
        this.deps.offlineReviewService
      ) {
        return await this.updateOfflineOnDemandItem(reviewId, itemId, data);
      }

      return await this.deps.onlineApi.updateOnDemandItem(reviewId, itemId, data);
    } catch (error) {
      if (options?.forceOnline) {
        throw error;
      }

      if (this.isWeb()) {
        throw error;
      }

      console.error('[ReviewService] API update failed, trying offline fallback:', error);
      return this.updateOfflineOnDemandItem(reviewId, itemId, data, error);
    }
  }

  async deleteOnDemandItem(reviewId: number, itemId: number): Promise<void> {
    return this.deps.onlineApi.deleteOnDemandItem(reviewId, itemId);
  }

  private async createOfflineOnDemandItem(
    reviewId: number,
    data: OnDemandItemInput,
    originalError?: unknown
  ): Promise<ReviewItem> {
    if (this.deps.offlineReviewService) {
      const itemId = await this.deps.offlineReviewService.createOfflineOnDemandItem(reviewId, data);
      return buildOfflineOnDemandReviewItem(reviewId, itemId, data);
    }

    if (originalError) {
      throw originalError;
    }

    throw new Error('Cannot create on-demand item offline - service not available');
  }

  private async updateOfflineOnDemandItem(
    reviewId: number,
    itemId: number,
    data: OnDemandItemUpdate,
    originalError?: unknown
  ): Promise<ReviewItem> {
    if (this.deps.offlineReviewService) {
      return await this.deps.offlineReviewService.updateOfflineOnDemandItem(reviewId, itemId, data);
    }

    if (originalError) {
      throw originalError;
    }

    throw new Error('Cannot update on-demand item offline - service not available');
  }

  private isWeb(): boolean {
    return this.deps.isWeb ? this.deps.isWeb() : Platform.OS === 'web';
  }
}
